import React, { useEffect } from "react";
import { Control, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { useToggle } from "react-use";

import { Collapse, Divider, Stack } from "@mui/material";

import { Button } from "@/components/form/Button/Button";
import { Select } from "@/components/form/select/Select";
import { ConditionalRender } from "@/lib/conditional-render/ConditionalRender";
import { projectIs } from "@/lib/feature-flags/featureFlagsDeside.helpers";
import { removeKeysWithNullableValues } from "@/utils/object.utils";

import { useSearchPageContext } from "../context";
import {
  useCitiesByCountry,
  useConvertCityOptions,
  useConvertToSelectOptionsSet,
  useSearchInputsOptions,
} from "../hooks";
import { SearchInputsSchema } from "../models";

type SelectOptions = {
  label: string;
  value: string;
};

const childrenOptions: SelectOptions[] = [
  {
    label: "No children",
    value: "1",
  },
  {
    label: "With children",
    value: "2",
  },
];

const smokingOptions: SelectOptions[] = [
  {
    label: "No",
    value: "1",
  },
  {
    label: "Yes",
    value: "2",
  },
];
type PropTypes = {
  control: Control;
  watch: UseFormWatch<SearchInputsSchema>;
  setValue: UseFormSetValue<SearchInputsSchema>;
};

export const MoreFilters: React.FC<PropTypes> = ({
  control,
  watch,
  setValue,
}) => {
  const [moreFiltersOpen, moreFiltersToggle] = useToggle(false);

  const { data: inputsData } = useSearchInputsOptions();

  const countryFormValue = watch("country");
  const { formValues } = useSearchPageContext();

  const { data: cities } = useCitiesByCountry(countryFormValue);
  const citiesOptions = useConvertCityOptions(cities);

  useEffect(() => {
    if (citiesOptions.find(({ value }) => value === formValues?.city)) {
      setValue("city", formValues?.city);
    } else {
      setValue("city", "");
    }
  }, [citiesOptions, formValues?.city, setValue]);

  useEffect(() => {
    const formKeys = removeKeysWithNullableValues(formValues || {});
    const isAnyKeyPresent = Object.values(inputNames).some((key) => {
      return inputNames[key] in formKeys;
    });

    if (isAnyKeyPresent) {
      moreFiltersToggle(true);
    }
  }, [formValues, moreFiltersToggle]);

  const inputsOptions = useConvertToSelectOptionsSet(inputsData);
  if (!inputsOptions) return null;

  return (
    <Stack spacing={1}>
      <Collapse
        in={moreFiltersOpen}
        timeout={500}
        easing={{
          enter: "cubic-bezier(0.680, -0.550, 0.265, 1.550)",
          exit: "cubic-bezier(0.680, -0.550, 0.265, 1.550)",
        }}
      >
        <Stack className="py-2" spacing={2}>
          <Select
            label="Country"
            options={inputsOptions.country}
            control={control}
            name={inputNames.country}
          />
          <Select
            label="City"
            options={citiesOptions}
            control={control}
            name={inputNames.city}
            disabled={!citiesOptions.length}
          />
          <Divider className="!mb-2 !mt-4" />
          <Select
            label="Eyes color"
            options={inputsOptions.eye}
            control={control}
            name={inputNames.eyeColor}
          />
          <Select
            label="Hair color"
            options={inputsOptions.hair}
            control={control}
            name={inputNames.hairColor}
          />
          <ConditionalRender on={!projectIs(["GM"])}>
            <Select
              label="Appearance type"
              options={inputsOptions.appearanceType}
              control={control}
              name={inputNames.appearanceType}
            />
          </ConditionalRender>

          <Divider className="!mb-2 !mt-4" />
          <Select
            label="Alcohol"
            options={inputsOptions.drinker}
            control={control}
            name={inputNames.drinking}
          />
          <Select
            label="Smoking"
            options={smokingOptions}
            control={control}
            name={inputNames.smoking}
          />
          <Divider className="!mb-2 !mt-4" />
          <Select
            label="Children"
            options={childrenOptions}
            control={control}
            name={inputNames.children}
          />
          <Select
            label="Religion"
            options={inputsOptions.religion}
            control={control}
            name={inputNames.religion}
          />
          <Select
            label="Zodiac sign"
            options={inputsOptions.zodiac}
            control={control}
            name={inputNames.zodiacSign}
          />
          <Select
            label="English speaking skills"
            options={inputsOptions.english}
            control={control}
            name={inputNames.englishSkill}
          />
        </Stack>
      </Collapse>
      <Button size="large" variant="secondary" onClick={moreFiltersToggle}>
        {moreFiltersOpen ? "less" : "more"} filters
      </Button>
    </Stack>
  );
};

const inputNames = {
  country: "country",
  city: "city",
  eyeColor: "eyeColor",
  hairColor: "hairColor",
  drinking: "drinking",
  smoking: "smoking",
  children: "children",
  religion: "religion",
  zodiacSign: "zodiacSign",
  englishSkill: "englishSkill",
  appearanceType: "appearanceType",
} as const;
